import { Plan } from '../api/middleware';

export interface DisplayPlan {
  name: string;
  dataAmount: string;
  dataUnit: string;
  description: string;
  appsIncluded: string[];
  monthlyPriceValue: number;
  price: string;
  benefitsCount: number;
  highlighted?: boolean;
  badge?: string;
}

export const PORTABILITY_BONUS_GB = 5;

/**
 * Sentinel value used when the API does not provide a badge for the
 * highlighted plan. Components (Plans, Hero) detect this sentinel and
 * swap it for the translated "most popular" label so the UI stays in
 * the active language. Real badges returned by the middleware are
 * rendered as-is.
 */
export const DEFAULT_PLAN_BADGE = 'Mais Procurado';
const DEFAULT_APPS = ['Whatsapp Ilimitado'];

export const defaultDisplayPlans: DisplayPlan[] = [
  {
    name: 'Start',
    dataAmount: '11',
    dataUnit: 'GB',
    description: '8GB do plano e mais 3GB na portabilidade.',
    appsIncluded: ['Whatsapp Ilimitado'],
    monthlyPriceValue: 29.99,
    price: 'R$ 29,99',
    benefitsCount: 0,
  },
  {
    name: 'Plus',
    dataAmount: '15',
    dataUnit: 'GB',
    description: '10GB do plano e mais 5GB na portabilidade.',
    appsIncluded: ['Whatsapp Ilimitado'],
    monthlyPriceValue: 44.9,
    price: 'R$ 44,90',
    benefitsCount: 0,
  },
  {
    name: 'Mega',
    dataAmount: '20',
    dataUnit: 'GB',
    description: '15GB do plano e mais 5GB na portabilidade.',
    appsIncluded: ['Whatsapp Ilimitado'],
    monthlyPriceValue: 54.9,
    price: 'R$ 54,90',
    benefitsCount: 0,
    highlighted: true,
    badge: DEFAULT_PLAN_BADGE,
  },
  {
    name: 'Ultra',
    dataAmount: '28',
    dataUnit: 'GB',
    description: '23GB do plano e mais 5GB na portabilidade.',
    appsIncluded: ['Whatsapp Ilimitado'],
    monthlyPriceValue: 64.9,
    price: 'R$ 64,90',
    benefitsCount: 0,
  },
  {
    name: 'Top',
    dataAmount: '50',
    dataUnit: 'GB',
    description: '45GB do plano e mais 5GB na portabilidade.',
    appsIncluded: ['Whatsapp Ilimitado'],
    monthlyPriceValue: 84.9,
    price: 'R$ 84,90',
    benefitsCount: 0,
  },
];

export function formatPriceNumber(value: number): string {
  return value.toFixed(2).replace('.', ',');
}

export function formatCurrency(value: number, includeSpace = true): string {
  return `R$${includeSpace ? ' ' : ''}${formatPriceNumber(value)}`;
}

export function getAnnualPlanPrice(value: number): number {
  return (value * 8) / 12;
}

export function formatAnnualPlanText(value: number): string {
  return `ou ${formatPriceNumber(getAnnualPlanPrice(value))} no plano anual`;
}

export function getHighlightedPlanIndex(totalPlans: number): number {
  return Math.floor(totalPlans / 2);
}

/**
 * Retorna o valor em GB/MB a ser exibido para o plano.
 *
 * Por padrão soma o bônus de portabilidade (PORTABILITY_BONUS_GB). Para
 * MVNEs Surf (isSurfMVNE=true), passe `skipPortabilityBonus: true` para
 * retornar apenas o valor original do plano, sem soma automática.
 */
export function getPlanDisplayValue(
  plan: Plan,
  options: { skipPortabilityBonus?: boolean } = {}
): string {
  const baseInternet = plan.internet_padrao || plan.plano;
  const match = baseInternet.match(/(\d+)\s*(GB|MB)/i);

  if (!match) {
    return plan.plano;
  }

  const [, amount, unit] = match;
  const base = parseInt(amount, 10);
  const displayAmount = options.skipPortabilityBonus
    ? base
    : base + PORTABILITY_BONUS_GB;

  return `${displayAmount} ${unit.toUpperCase()}`;
}

function getPlanDisplayParts(planValue: string) {
  const match = planValue.match(/^(\d+)\s*(\w+)/);

  return {
    dataAmount: match ? match[1] : planValue,
    dataUnit: match ? match[2].toUpperCase() : 'GB',
  };
}

export function getPlanDisplayName(plan: Plan, displayPlan: string) {
  const trimmedPlanName = plan.nome_plano?.trim() || '';
  const isInternetValueName = /^(\d+)\s*(GB|MB)$/i.test(trimmedPlanName);

  return isInternetValueName ? displayPlan : plan.nome_plano;
}

/**
 * Mapeia planos da API para o formato de exibição.
 *
 * Por padrão soma o bônus de portabilidade ao valor exibido. Para
 * MVNEs Surf (isSurfMVNE=true), passe `skipPortabilityBonus: true` para
 * exibir o valor original do plano sem a soma automática.
 */
export function mapApiPlansToDisplay(
  apiPlans: Plan[],
  options: { skipPortabilityBonus?: boolean } = {}
): DisplayPlan[] {
  const highlightedIndex = getHighlightedPlanIndex(apiPlans.length);

  return apiPlans.map((plan, index) => {
    const displayPlan = getPlanDisplayValue(plan, options);
    const { dataAmount, dataUnit } = getPlanDisplayParts(displayPlan);
    const monthlyPriceValue = Number(plan.valor) || 0;

    return {
      name: getPlanDisplayName(plan, displayPlan),
      dataAmount,
      dataUnit,
      description: plan.descricao_detalhamento || plan.detalhamento || '',
      appsIncluded: plan.aplicativos?.length ? plan.aplicativos : DEFAULT_APPS,
      monthlyPriceValue,
      price: formatCurrency(monthlyPriceValue),
      benefitsCount: plan.beneficios?.length || 0,
      highlighted: index === highlightedIndex,
      badge: index === highlightedIndex ? DEFAULT_PLAN_BADGE : undefined,
    };
  });
}

export function getHighlightedDisplayPlan(plans: DisplayPlan[]): DisplayPlan | null {
  if (!plans.length) {
    return null;
  }

  return plans.find((plan) => plan.highlighted) || plans[getHighlightedPlanIndex(plans.length)];
}
